import React, { useState } from 'react';
import { Form, Input, InputNumber, Select, Switch, Button, Card, Space, Divider, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import DrugFormItem from './components/DrugFormItem';
import DiseaseFormItem from './components/DiseaseFormItem';
import EndpointFormItem from './components/EndpointFormItem';
import { ValidationTaskV3 } from '../../../services/dataService';
import { useMutation, useQueryClient } from 'react-query';
import { CheckCircle2Icon } from 'lucide-react';

const { Option } = Select;

const studyTypes = [
  { label: 'Clinical Trial: RCT', value: 'Clinical Trial: RCT' },
  { label: 'Clinical Trial: non-RCT', value: 'Clinical Trial: non-RCT' },
  { label: 'Review: meta-analysis', value: 'Review: meta-analysis' },
  { label: 'Observational study', value: 'Observational study' },
  { label: 'Review: other', value: 'Review: other' },
  { label: 'Case study', value: 'Case study' },
  { label: 'Preclinical', value: 'Preclinical' },
  { label: 'Other', value: 'Other' },
];

const GenericForm = ({ initialValues, validationTaskId, validator, articleId, isValidated }) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const saveMutation = useMutation(
    (values) => {
      return ValidationTaskV3.saveGenericAnnotation(validationTaskId, {
        ...values,
        validation_task_id: validationTaskId,
        article_id: articleId,
        validator,
      });
    },
    {
      onSuccess: () => {
        message.success('Annotation saved successfully');
        queryClient.invalidateQueries('validation_task');
      },
    },
  );

  const onFinish = (values) => {
    // console.log('Form values:', values);
    saveMutation.mutate(values);
  };

  const clearForm = () => {
    form.resetFields();
    form.setFieldsValue({
      ...initialValues,
    });
  };

  return (
    <Card
      title={
        <div className="flex justify-between">
          <div className={isValidated ? 'text-green-500 flex items-center gap-2' : ''}>
            Validation Generic Annotation Form
            {isValidated && <CheckCircle2Icon className="w-4 h-4" />}
          </div>
          <Button onClick={clearForm} type="link" className="text-red-500 hover:text-red-600">
            Clear
          </Button>
        </div>
      }
      style={{ maxWidth: 800, margin: '20px auto' }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          ...initialValues,
        }}
      >
        <div className="text-sm font-semibold mt-4 mb-2">Drugs</div>
        <Form.List name="drugs" label="Drugs">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Card key={key} style={{ marginBottom: 16 }}>
                  <DrugFormItem name={name} restField={restField} onRemove={() => remove(name)} />
                  <Space
                    style={{ display: 'flex', marginBottom: 8, alignItems: 'start', justifyContent: 'space-between' }}
                    align="baseline"
                    split={<Divider type="vertical" className="h-16" />}
                  >
                    <Form.Item {...restField} name={[name, 'is_studied']} label="Is Studied" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'is_combination']}
                      label="Is Combination"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'is_randomized']}
                      label="Is Randomized"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Space>
                </Card>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Drug
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <div className="text-sm font-semibold mt-4 mb-2">Endpoints</div>
        <Form.List name="endpoints">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Card key={key} style={{ marginBottom: 16 }}>
                  <EndpointFormItem name={name} restField={restField} onRemove={() => remove(name)} />
                </Card>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Endpoint
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <div className="text-sm font-semibold mt-4 mb-2">Diseases</div>
        <Form.List name="diseases">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Card key={key} style={{ marginBottom: 16 }}>
                  <DiseaseFormItem name={name} restField={restField} onRemove={() => remove(name)} />
                </Card>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Disease
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Form.Item
          name="num_patients"
          label="Number of Patients"
          rules={[{ required: false, message: 'Please input number of patients!' }]}
        >
          <Input min={0} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="study_type"
          label="Study Type"
          rules={[{ required: true, message: 'Please select study type!' }]}
        >
          <Select>
            {studyTypes.map((studyType) => (
              <Option key={studyType.value} value={studyType.value}>
                {studyType.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="is_adhoc" label="Is Adhoc/Posthoc" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit">
            Submit
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default GenericForm;
